// =============================================================================
// src/auth/authEvents.js
// What an onAuthStateChange event means for the app.
//
// supabase-js emits SIGNED_IN for more than a sign-in. It also fires when a
// stored session is recovered: every time the tab becomes visible again
// (_onVisibilityChanged -> _recoverAndRefresh), and when another tab relays
// its own SIGNED_IN over the BroadcastChannel. Until Oct 2026 AuthProvider
// treated every SIGNED_IN as a login: it wrote a "Login" audit row (one day of
// production: 65 Login rows for 15 real sign-ins) and reloaded the profile.
// On a phone woken after the idle limit, that reload raced the
// automatic sign-out and went out with only the anon key: a burst of 401 /
// 42501 errors on super_admins and get_effective_capabilities after every
// idle sign-out, plus a false "Login" row at the same instant as the Logout.
//
// The rule: an event is a login only when it moves this tab from a known
// signed-out state (knownUid === null) or from another account to a user.
// knownUid is undefined until the tab has learned its starting state, and an
// event that arrives before then is a recovery, never a login.
// =============================================================================

// knownUid: the user id this tab already holds (null = signed out, undefined =
// not known yet). nextUid: the user id carried by the event, or null.
export function classifyAuthEvent(event, knownUid, nextUid) {
  if (event === "SIGNED_OUT" || !nextUid) {
    return { signedOut: true, isLogin: false, reloadProfile: false };
  }
  const changed = knownUid !== nextUid;
  if (event === "SIGNED_IN") {
    return {
      signedOut: false,
      // Recovery of the same account (tab wake, other tab) is not a login.
      isLogin: changed && knownUid !== undefined,
      reloadProfile: changed,
    };
  }
  if (event === "INITIAL_SESSION") {
    return { signedOut: false, isLogin: false, reloadProfile: changed };
  }
  // TOKEN_REFRESHED, USER_UPDATED, MFA_CHALLENGE_VERIFIED, PASSWORD_RECOVERY:
  // same account, nothing to reload (the focus refresher covers drift).
  return { signedOut: false, isLogin: false, reloadProfile: false };
}
